/**
 * @module cercaliagl/layer/LandMarks3D
 */
/* eslint-disable quote-props */
import { URL_DATA_MODELS } from '../globals.js';
import { LayersId } from './Base';
/**
 * @typedef {Object} LandMarks3DOptions
 * @property {string} id Id
 * @property {number} map Map.
 * @property {number} minZoom Min zoom. Default `15`.
 * @property {number} maxZoom Max zoom. Default `19`.
 * @property {boolean} visible Visible. Default `false`.
 */
/**
 * @classdesc
 */
var LandMarks3D = /** @class */ (function () {
    /**
     * @constructor
     * @param {import("maplibre-gl").Map} map Map
     */
    function LandMarks3D(options) {
        var _this = this;
        /**
         * @private
         * @type {string}
         */
        this.id_ = LayersId.LANDMARKS3D;
        /**
         * @private
         * @type {import("maplibre-gl").Map}
         */
        this.map_ = options.map;
        /**
         * @private
         * @type {boolean}
         */
        this.visible_ = options.visible !== undefined ? options.visible : false;
        /**
         * @private
         * @type {Array.<Object>}
         */
        this.registerColladas_ = [];
        /**
         * @private
         * @type {Array.<Object>}
         */
        this.landMarkVisibles = [];
        /**
         * @private
         * @type {number}
         */
        this.minZoom_ = options.minZoom !== undefined ? options.minZoom : 15;
        /**
         * @private
         * @type {number}
         */
        this.maxZoom_ = options.maxZoom !== undefined ? options.maxZoom : 20;
        /**
         * @private
         * @type {string}
         */
        this.landMarkSourceLayer = '3dlandmarks';
        /**
         * @private
         * @type {Threebox}
         */
        this.threebox_ = null;
        /**
         * @private
         * @type {Object}
         */
        this.landMarkLayer_ = {
            id: LayersId.LANDMARKS3DMODELS,
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                _this.threebox_ = new window.Threebox(map, gl, { defaultLights: true });
            },
            render: function (gl) {
                gl.clear(gl.DEPTH_BUFFER_BIT);
                _this.update();
            },
        };
        this.map_.on('ready', function () {
            _this.map_.setLayerZoomRange(LayersId.LANDMARKS3DMODELS, 15, 20); // mapbox bug: when type=custom, minzoom doesn't work
            _this.map_.on('sourcedata', function (e) {
                if (_this.map_.getLayer(_this.id_) && _this.map_.areTilesLoaded() && e.sourceId !== 'mapbox-gl-draw-hot') {
                    _this.updateBuildings_();
                }
            });
            // Refrescar threebox.
            _this.map_.on('dragstart', _this.update);
            _this.map_.on('drag', _this.update);
            _this.map_.on('dragend', _this.update);
            _this.map_.on('moveend', _this.update);
            _this.map_.on('move', _this.update);
            _this.map_.on('3denabled', function () {
                setTimeout(_this.update, 1250);
                setTimeout(_this.update, 2500);
            });
        });
    }
    /**
     * @private
     * @param {Object} object Object
     * @param {number} degreeX Degree X
     * @param {number} degreeY Degree Y
     * @param {number} degreeZ Degree Z
     */
    LandMarks3D.rotateObject = function (object, degreeX, degreeY, degreeZ) {
        object.rotateX((degreeX * Math.PI) / 180);
        object.rotateY((degreeY * Math.PI) / 180);
        object.rotateZ((degreeZ * Math.PI) / 180);
    };
    /**
   * @private
   * @param {string} oid Oid,
   * @return {boolean} Removed or not
   */
    LandMarks3D.prototype.removeByOID_ = function (oid) {
        var removed = false;
        for (var i = this.registerColladas_.length - 1; i >= 0; i -= 1) {
            if (this.registerColladas_[i].oid === oid) {
                this.registerColladas_.splice(i, 1);
                removed = true;
                return removed;
            }
        }
        return removed;
    };
    /**
     * @private
     * @param {Object} group Group.
     */
    LandMarks3D.prototype.removeDAE_ = function (group) {
        if (this.removeByOID_(group.oid)) {
            group.traverse(function (node) {
                if (node instanceof window.Threebox.THREE.Mesh) {
                    if (node.geometry instanceof window.Threebox.THREE.BufferGeometry) {
                        Object.keys(node.geometry.attributes).forEach(function (name) {
                            node.geometry.removeAttribute(name);
                        });
                    }
                    node.geometry.dispose();
                    if (node.material && node.material.map) {
                        node.material.map.dispose();
                    }
                    node.material.dispose();
                    node.parent.remove(node);
                    node = undefined; // eslint-disable-line no-param-reassign
                }
            });
            this.threebox_.remove(group.parent);
        }
        else {
            console.warn("Can't not remove ".concat(group.oid)); // eslint-disable-line no-console
        }
    };
    /**
     * @private
     * @param {string} oid Oid
     * @return {string} Id
     */
    LandMarks3D.prototype.getModelById_ = function (oid) {
        for (var i = 0, iLen = this.registerColladas_.length; i < iLen; i += 1) {
            if (this.registerColladas_[i].oid === oid) {
                return this.registerColladas_[i];
            }
        }
        return null;
    };
    LandMarks3D.prototype.update = function () {
        if (this.threebox_) {
            this.threebox_.update();
        }
    };
    /**
     * @private
     * @param {Object} dae Dae.
     */
    LandMarks3D.prototype.loadDAE_ = function (dae) {
        var _this = this;
        var model;
        var id = dae.id;
        var coords = [
            dae.coords.coordX,
            dae.coords.coordY,
            dae.coords.coordZ,
        ];
        var loadingManager = new window.Threebox.THREE.LoadingManager(function () {
            LandMarks3D.rotateObject(model, 90, 180, 0);
            if (_this.landMarkVisibles.indexOf(id) >= 0) {
                var obj = _this.threebox_.addAtCoordinate(model, coords, { scaleToLatitude: true, preScale: 1 });
                obj.oid = id;
                _this.registerColladas_.push(obj);
            }
            _this.update();
        });
        // collada
        var loader = new window.Threebox.THREE.ColladaLoader(loadingManager);
        var url = "".concat(URL_DATA_MODELS, "/data/models/").concat(dae.href);
        loader.load(url, function (collada) {
            model = collada.scene;
        });
    };
    /**
     * @private
     */
    LandMarks3D.prototype.updateBuildings_ = function () {
        var _this = this;
        var features = this.map_.queryRenderedFeatures({ layers: [LayersId.LANDMARKS3D] });
        this.landMarkVisibles = [];
        features.forEach(function (feature) {
            var oid = _this.getModelById_(feature.id);
            if (!oid) {
                _this.loadDAE_({
                    id: feature.id,
                    coords: {
                        coordX: feature.properties.coordx,
                        coordY: feature.properties.coordy,
                        coordZ: 0,
                    },
                    href: feature.properties.href,
                });
            }
            _this.landMarkVisibles.push(feature.id);
        });
        // busuqem el que tenim i si hem de borrar obj dae
        for (var i = this.registerColladas_.length - 1; i >= 0; i -= 1) {
            var value = this.registerColladas_[i];
            if (this.landMarkVisibles.indexOf(value.oid) < 0) {
                this.removeDAE_(value);
            }
        }
    };
    /**
     * @return {string} Id.
     */
    LandMarks3D.prototype.getId = function () {
        return this.id_;
    };
    /**
     * Set layer visible or not.
     * @param {boolean} visible Visible `true`, not visible `false`.
     * @api
     */
    LandMarks3D.prototype.setVisible = function (visible) {
        this.visible_ = visible;
        var value = visible ? 'visible' : 'none';
        this.map_.setLayoutProperty(LayersId.LANDMARKS3D, 'visibility', value);
        this.map_.setLayoutProperty(LayersId.LANDMARKS3DMODELS, 'visibility', value);
    };
    /**
     * Is layer visible
     * @return {boolean} Visible `true`, not visible `false`.
     * @api
     */
    LandMarks3D.prototype.isVisible = function () {
        return this.visible_;
    };
    /**
     * @return {boolean} Min zoom
     */
    LandMarks3D.prototype.getMinZoom = function () {
        return this.minZoom_;
    };
    /**
     * @return {object} Min zoom
     */
    LandMarks3D.prototype.getLayer = function () {
        return this.layer_;
    };
    /**
     * @param {Map} map Map
     */
    LandMarks3D.prototype.setMap = function (map) {
        if (map) {
            this.map_ = map.getMapboxglMap();
            this.map_.addLayer(this.landMarkLayer_);
        }
        else {
            this.map_.removeLayer(LayersId.LANDMARKS3DMODELS);
        }
    };
    return LandMarks3D;
}());
export default LandMarks3D;
//# sourceMappingURL=LandMarks3D.js.map